import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { ToastService } from '../../../core/ui/toast.service';
import { DatePicker } from '../../../shared/date-picker/date-picker';
import { PageHeader } from '../../../shared/page-header/page-header';

interface EmailTemplate {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  subject: string | null;
  body: string | null;
  version: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  /** Falso enquanto ninguém reescrever: o que sai é o texto que vem no código. */
  readonly customized: boolean;
  readonly updatedBy: string | null;
  readonly placeholders: Record<string, string>;
}

/**
 * Textos dos e-mails automáticos.
 *
 * <p>A lista é fechada — cada papel é escolhido pelo sistema conforme o caso, não por quem
 * escreve. O que se edita é o texto, e repor devolve o que vem no código.
 */
@Component({
  selector: 'app-mail-templates',
  imports: [FormsModule, RouterLink, PageHeader, DatePicker, DatePipe],
  templateUrl: './mail-templates.html',
})
export class MailTemplates implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly toasts = inject(ToastService);

  protected readonly templates = signal<EmailTemplate[]>([]);
  protected readonly loading = signal(true);
  protected readonly saving = signal<string | null>(null);
  protected readonly error = signal<string | null>(null);
  /** Qual está aberto para edição — um de cada vez, que são textos longos. */
  protected readonly open = signal<string | null>(null);
  protected readonly pendingReset = signal<EmailTemplate | null>(null);

  /** Envio de teste: para onde vai, e o que o servidor respondeu. */
  protected testTo = '';
  protected readonly testing = signal<string | null>(null);
  protected readonly testResult = signal<{ key: string; sent: boolean; message: string } | null>(null);

  ngOnInit(): void {
    this.load();
  }

  protected toggle(key: string): void {
    this.open.update((current) => (current === key ? null : key));
  }

  protected chaves(template: EmailTemplate): string[] {
    return Object.keys(template.placeholders);
  }

  protected save(template: EmailTemplate): void {
    if (!template.body?.trim()) {
      this.error.set('Escreva o texto do e-mail.');
      return;
    }
    this.error.set(null);
    this.saving.set(template.key);
    this.http.put<EmailTemplate>(`${this.base()}/${template.key}`, {
      subject: template.subject,
      body: template.body,
      version: template.version,
      approvedAt: template.approvedAt,
      approvedBy: template.approvedBy,
    }).subscribe({
      next: () => {
        this.saving.set(null);
        this.load();
        this.toasts.success('Modelo guardado.');
      },
      error: (response) => {
        this.saving.set(null);
        const fields = (response?.error?.fieldErrors ?? []).map((f: { message: string }) => f.message);
        this.error.set(fields.length ? fields.join(' ') : 'Não foi possível guardar o modelo.');
      },
    });
  }

  /**
   * Manda o que está no ecrã, mesmo por gravar.
   *
   * <p>É a ordem certa para um texto com peso legal: vê-se como fica antes de entrar em vigor,
   * e não depois.
   */
  protected sendTest(template: EmailTemplate): void {
    if (!this.testTo.trim()) return;
    this.testResult.set(null);
    this.testing.set(template.key);
    this.http.post<{ sent: boolean; message: string }>(`${this.base()}/${template.key}/teste`, {
      to: this.testTo.trim(),
      subject: template.subject,
      body: template.body,
    }).subscribe({
      next: (result) => {
        this.testResult.set({ key: template.key, ...result });
        this.testing.set(null);
      },
      error: (response) => {
        this.testResult.set({
          key: template.key,
          sent: false,
          message: response?.error?.message ?? 'Não foi possível enviar o teste.',
        });
        this.testing.set(null);
      },
    });
  }

  protected askReset(template: EmailTemplate): void {
    this.pendingReset.set(template);
  }

  protected cancelReset(): void {
    this.pendingReset.set(null);
  }

  /** Repor não perde nada: o texto do código é o aprovado de origem. */
  protected confirmReset(): void {
    const template = this.pendingReset();
    if (!template) return;
    this.pendingReset.set(null);
    this.saving.set(template.key);
    this.http.delete(`${this.base()}/${template.key}`).subscribe({
      next: () => {
        this.saving.set(null);
        this.load();
        this.toasts.success('Modelo reposto.');
      },
      error: () => {
        this.saving.set(null);
        this.error.set('Não foi possível repor o modelo.');
      },
    });
  }

  private load(): void {
    this.http.get<EmailTemplate[]>(this.base()).subscribe({
      next: (list) => {
        this.templates.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Não foi possível carregar os modelos.');
        this.loading.set(false);
      },
    });
  }

  private base(): string {
    return `/api/t/${this.auth.currentTenant()}/configuracoes/modelos-email`;
  }
}
